import { useMutation } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useEnqueueToast, useLanguage, useTranslate } from '@orus.eu/pharaoh'
import { isSuccess } from '@orus.eu/result'
import type { Language } from '@orus.eu/translations'

import { useTRPC } from '../../client.ts'
import { useSession } from '../session.ts'
import { createTrackingMessage, sendMessage, waitForTrackingQueueToBeEmpty } from '../tracking/tracking.ts'

import { useAsyncCallback } from '#/lib/hooks/use-async-callback.ts'

/**
 * Persists the chosen language on the current session/account and reloads so it applies
 * everywhere. Shared by the back-office navbar, the self-onboarding funnel and the client
 * portal account page.
 */
export function useSelectLanguage(): (language: Language, subscriptionId?: string) => void {
  const trpc = useTRPC()
  const { user } = useSession()
  const currentLanguage = useLanguage()
  const { enqueueToast } = useEnqueueToast()
  const translate = useTranslate()
  const { mutateAsync: updateMyLanguage } = useMutation(trpc.users.updateMyLanguage.mutationOptions())

  return useAsyncCallback(
    useCallback(
      async (language: Language, subscriptionId?: string) => {
        if (language === currentLanguage) return
        const result = await updateMyLanguage({ language })
        if (!isSuccess(result)) {
          enqueueToast(translate('update_error'), { variant: 'danger' })
          return
        }

        if (!user && subscriptionId) {
          sendMessage(
            createTrackingMessage({
              event: 'prospect_language_updated',
              subscription_id: subscriptionId,
              user_language: language,
            }),
          )
          // Wait for the tracking queue to be empty before navigating
          await waitForTrackingQueueToBeEmpty()
        }

        window.location.reload()
      },
      [currentLanguage, enqueueToast, translate, updateMyLanguage, user],
    ),
  )
}
